import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import {
  Container,
  Card,
  Group,
  Text,
  Avatar,
  Button,
  Stack,
  Center,
  Alert,
  Tabs,
  Menu,
  ActionIcon,
  Modal,
  UnstyledButton,
  ScrollArea,
  Skeleton,
  Badge,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconStar,
  IconUserPlus,
  IconUserMinus,
  IconCalendar,
  IconDotsVertical,
  IconFlag,
  IconSend2,
  IconUser,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { usersService } from '../../services/users';
import { ReviewCard } from '../../components/reviews';
import { ReportModal } from '../../components/common/ReportModal';
import type { UserProfile, Review, User, PaginatedResult } from '../../types';
import { getBadgeMeta } from '../../utils/badges';

function formatJoinDate(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function UserProfilePage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Follower/following modal state
  const [followModalType, setFollowModalType] = useState<'followers' | 'following' | null>(null);

  const isMobile = !useMediaQuery('(min-width: 576px)');
  const isOwnProfile = currentUser?.username === username;

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['user-profile', username],
    queryFn: () => usersService.getProfileByUsername(username!),
    enabled: !!username,
    staleTime: 60_000,
  });

  const reviews = profile?.reviews ?? [];
  const reviewCount = reviews.length;

  const { data: followListData, isLoading: isLoadingFollowList } = useQuery({
    queryKey: ['follow-list', profile?.id, followModalType],
    queryFn: (): Promise<PaginatedResult<User>> => followModalType === 'followers'
      ? usersService.getFollowers(profile!.id)
      : usersService.getFollowing(profile!.id),
    enabled: !!profile?.id && followModalType !== null,
    staleTime: 30_000,
  });
  const followList: User[] = followListData?.items ?? [];

  const isFollowing = profile?.isFollowing ?? false;

  const followMutation = useMutation({
    mutationFn: () => usersService.follow(profile!.id),
    onSuccess: () => {
      queryClient.setQueryData(['user-profile', username], (old: UserProfile | undefined) =>
        old ? { ...old, followerCount: old.followerCount + 1, isFollowing: true } : old
      );
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update follow status',
        color: 'red',
      });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: () => usersService.unfollow(profile!.id),
    onSuccess: () => {
      queryClient.setQueryData(['user-profile', username], (old: UserProfile | undefined) =>
        old ? { ...old, followerCount: old.followerCount - 1, isFollowing: false } : old
      );
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update follow status',
        color: 'red',
      });
    },
  });

  const isFollowLoading = followMutation.isPending || unfollowMutation.isPending;

  const handleFollowToggle = () => {
    if (!profile?.id || !isAuthenticated || isFollowLoading) return;
    if (isFollowing) {
      modals.openConfirmModal({
        title: 'Unfollow',
        children: <Text size="sm">Unfollow @{profile.username}?</Text>,
        labels: { confirm: 'Unfollow', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => unfollowMutation.mutate(),
      });
    } else {
      followMutation.mutate();
    }
  };

  const handleDeleteReview = (reviewId: string) => {
    queryClient.setQueryData(['user-profile', username], (old: UserProfile | undefined) =>
      old ? { ...old, reviews: old.reviews.filter((r: Review) => r.id !== reviewId) } : old
    );
  };

  const handleCopyProfileLink = async () => {
    const url = `${window.location.origin}/users/${profile?.username}`;
    try {
      await navigator.clipboard.writeText(url);
      notifications.show({ title: 'Link copied', message: 'Profile link copied to clipboard', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to copy link', color: 'red' });
    }
  };

  if (isLoading) {
    return (
      <Container fluid px={{ base: 'md', md: 'xl' }} pt="md" pb="xl">
        <Group gap="lg" mb="xl">
          <Skeleton circle height={100} />
          <Stack gap="xs" style={{ flex: 1 }}>
            <Skeleton height={24} width={200} />
            <Skeleton height={16} width={300} />
            <Skeleton height={16} width={150} />
          </Stack>
        </Group>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} height={120} mb="md" radius="md" />
        ))}
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container fluid px={{ base: 'md', md: 'xl' }} pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {error ? 'Failed to load profile. Please try again.' : 'User not found'}
          <Button variant="subtle" size="xs" mt="xs" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px={{ base: 'md', md: 'xl' }} pt="md" pb="xl">
      {/* Profile Card */}
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="xl">
        <Stack gap="md">
          {/* Avatar + name + bio row — stacks on mobile */}
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Group gap="lg" align="flex-start" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
              <Avatar
                src={profile.profileImageUrl}
                alt={profile.username}
                size={isMobile ? 72 : 100}
                radius="xl"
                style={{ flexShrink: 0 }}
              />

              <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <Text size={isMobile ? 'lg' : 'xl'} fw={700}>
                  @{profile.username}
                </Text>

                {profile.displayName && profile.displayName !== profile.username && (
                  <Text size="sm" c="dimmed">
                    {profile.displayName}
                  </Text>
                )}

                {profile.bio && (
                  <Text size="sm" c="dimmed">
                    {profile.bio}
                  </Text>
                )}

                {profile.createdAt && (
                  <Group gap={6}>
                    <IconCalendar size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                    <Text size="sm" c="dimmed">
                      Joined {formatJoinDate(profile.createdAt)}
                    </Text>
                  </Group>
                )}

                {profile.badges && profile.badges.length > 0 && (
                  <Group gap="xs" mt="xs">
                    {profile.badges.map((badge) => {
                      const meta = getBadgeMeta(badge.key);
                      return (
                        <Tooltip key={badge.key} label={`${meta.label} · ${meta.description}`} withArrow>
                          <Badge color={meta.color} variant="light">
                            {meta.emoji} {badge.name}
                          </Badge>
                        </Tooltip>
                      );
                    })}
                  </Group>
                )}
              </Stack>
            </Group>

            {/* Action buttons — inline on desktop, move below on mobile */}
            {!isMobile && (
              <Group gap="xs" style={{ flexShrink: 0 }}>
                {!isOwnProfile && isAuthenticated && (
                  <Button
                    variant={isFollowing ? 'outline' : 'filled'}
                    leftSection={isFollowing ? <IconUserMinus size={16} /> : <IconUserPlus size={16} />}
                    onClick={handleFollowToggle}
                    loading={isFollowLoading}
                  >
                    {isFollowing ? 'Unfollow' : 'Follow'}
                  </Button>
                )}
                {isOwnProfile && (
                  <Button variant="outline" onClick={() => navigate('/settings')}>
                    Edit Profile
                  </Button>
                )}
                <Menu shadow="md" position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDotsVertical size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconSend2 size={16} />} onClick={handleCopyProfileLink}>Copy link</Menu.Item>
                    {!isOwnProfile && isAuthenticated && (
                      <>
                        <Menu.Divider />
                        <Menu.Item leftSection={<IconFlag size={16} />} color="red" onClick={() => setReportModalOpen(true)}>Report user</Menu.Item>
                      </>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </Group>

          {/* Stats row */}
          <Group gap="xl" wrap="wrap">
            <Stack gap={0} align="center">
              <Text size="lg" fw={600}>{reviewCount}</Text>
              <Text size="xs" c="dimmed">Reviews</Text>
            </Stack>
            <UnstyledButton onClick={() => setFollowModalType('followers')}>
              <Stack gap={0} align="center">
                <Text size="lg" fw={600}>{profile.followerCount}</Text>
                <Text size="xs" c="dimmed">Followers</Text>
              </Stack>
            </UnstyledButton>
            <UnstyledButton onClick={() => setFollowModalType('following')}>
              <Stack gap={0} align="center">
                <Text size="lg" fw={600}>{profile.followingCount}</Text>
                <Text size="xs" c="dimmed">Following</Text>
              </Stack>
            </UnstyledButton>
            {profile.averageRating != null && (
              <Stack gap={0} align="center">
                <Text size="lg" fw={600}>⭐ {profile.averageRating.toFixed(1)}</Text>
                <Text size="xs" c="dimmed">Avg Rating</Text>
              </Stack>
            )}

            {/* Action buttons inline with stats on mobile */}
            {isMobile && (
              <Group gap="xs" style={{ marginLeft: 'auto' }}>
                {!isOwnProfile && isAuthenticated && (
                  <Button
                    size="xs"
                    variant={isFollowing ? 'outline' : 'filled'}
                    leftSection={isFollowing ? <IconUserMinus size={14} /> : <IconUserPlus size={14} />}
                    onClick={handleFollowToggle}
                    loading={isFollowLoading}
                  >
                    {isFollowing ? 'Unfollow' : 'Follow'}
                  </Button>
                )}
                {isOwnProfile && (
                  <Button size="xs" variant="outline" onClick={() => navigate('/settings')}>Edit Profile</Button>
                )}
                <Menu shadow="md" position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="sm">
                      <IconDotsVertical size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconSend2 size={16} />} onClick={handleCopyProfileLink}>Copy link</Menu.Item>
                    {!isOwnProfile && isAuthenticated && (
                      <>
                        <Menu.Divider />
                        <Menu.Item leftSection={<IconFlag size={16} />} color="red" onClick={() => setReportModalOpen(true)}>Report user</Menu.Item>
                      </>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </Group>
        </Stack>
      </Card>

      {/* Reviews */}
      <Tabs defaultValue="reviews">
        <Tabs.List>
          <Tabs.Tab value="reviews" leftSection={<IconStar size={16} />}>
            Reviews ({reviewCount})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="reviews" pt="md">
          <Stack gap="md">
            {reviews.map((review: Review) => (
              <ReviewCard
                key={review.id}
                review={review}
                showProduct
                onDelete={handleDeleteReview}
              />
            ))}

            {reviews.length === 0 && (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <Text c="dimmed" size="sm">
                    {isOwnProfile ? "You haven't written any reviews yet." : `@${profile.username} hasn't written any reviews yet.`}
                  </Text>
                  {isOwnProfile && (
                    <Button component={Link} to="/reviews/create" size="xs" radius="xl" variant="subtle">
                      Write your first review
                    </Button>
                  )}
                </Stack>
              </Center>
            )}

          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Report Modal */}
      <ReportModal
        opened={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        targetId={profile.id}
        targetType="User"
        targetUserId={profile.id}
        targetName={`@${profile.username}`}
      />

      {/* Followers / Following Modal */}
      <Modal
        opened={followModalType !== null}
        onClose={() => setFollowModalType(null)}
        title={followModalType === 'followers' ? 'Followers' : 'Following'}
        size="sm"
      >
        {isLoadingFollowList ? (
          <Center py="xl">
            <Skeleton height={16} mb="xs" />
            <Skeleton height={16} mb="xs" />
            <Skeleton height={16} />
          </Center>
        ) : followList.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">
              {followModalType === 'followers' ? 'No followers yet' : 'Not following anyone yet'}
            </Text>
          </Center>
        ) : (
          <ScrollArea.Autosize mah={400}>
            <Stack gap="xs">
              {followList.map((u) => (
                <UnstyledButton
                  key={u.id}
                  onClick={() => { setFollowModalType(null); navigate(`/users/${u.username}`); }}
                  py="xs"
                  px="sm"
                  style={{ borderRadius: 'var(--mantine-radius-sm)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <Group gap="sm">
                    <Avatar src={u.profileImageUrl} alt={u.username} size="md" radius="xl">
                      <IconUser size={18} />
                    </Avatar>
                    <div>
                      <Text size="sm" fw={600}>@{u.username}</Text>
                      {u.displayName && u.displayName !== u.username && (
                        <Text size="xs" c="dimmed">{u.displayName}</Text>
                      )}
                    </div>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Modal>
    </Container>
  );
}
